import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Mail, ChevronDown } from "lucide-react";

const productLinks = [
  { label: "Chatro", href: "/products/chatro" },
  { label: "EduSphere", href: "/products/edusphere" },
  { label: "TeamSync", href: "/products/teamsync" },
  { label: "ClientStream", href: "/products/clientstream" },
];

const serviceLinks = [
  { label: "Web Development", href: "/services/web-development" },
  { label: "App Development", href: "/services/app-development" },
  // { label: "Game Development", href: "/services/game-development" },
  { label: "Creative Designs", href: "/services/creative-designs" },
  { label: "Digital Marketing", href: "/services/digital-marketing" },
  { label: "AI Development", href: "/services/ai-development" },
  { label: "Content Marketing", href: "/services/content-marketing" },
  { label: "Blockchain Development", href: "/services/blockchain-development" },
  { label: "ERP Solutions", href: "/services/erp-solutions" },
];

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about" },
  { label: "Products", href: "#", dropdown: "products" },
  { label: "Services", href: "#", dropdown: "services" },
  { label: "Case Studies", href: "/case-studies" },
  { label: "Contact Us", href: "/contact" },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setActiveDropdown(null);
    setMobileDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDropdownItems = (type) => type === "products" ? productLinks : serviceLinks;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "py-3 bg-[#08080f]/90 backdrop-blur-xl shadow-lg shadow-purple-900/5" : "py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between" ref={navRef}>
        {/* Logo */}
        <Link to="/" className="flex items-center" data-testid="logo-link">
          <img src="/logo-white.png" alt="Digitronix Consultancy" className="h-8 sm:h-12 w-auto" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center glass-nav rounded-full px-8 py-3 gap-8">
          {navLinks.map((link) =>
            link.dropdown ? (
              <div key={link.label} className="relative">
                <button
                  onClick={() => setActiveDropdown(activeDropdown === link.dropdown ? null : link.dropdown)}
                  className={`text-sm font-medium transition-colors hover:text-purple-400 flex items-center gap-1 ${
                    (link.dropdown === "services" && location.pathname.startsWith("/services")) ||
                    (link.dropdown === "products" && location.pathname.startsWith("/products"))
                      ? "text-white" : "text-gray-400"
                  }`}
                  data-testid={`nav-${link.dropdown}-dropdown`}
                >
                  {link.label}
                  <ChevronDown size={14} className={`transition-transform ${activeDropdown === link.dropdown ? "rotate-180" : ""}`} />
                </button>

                {activeDropdown === link.dropdown && (
                  <div className={`absolute top-full left-1/2 -translate-x-1/2 mt-4 ${link.dropdown === "services" ? "w-[520px]" : "w-[280px]"} bg-[#0d0d1a] border border-purple-900/30 rounded-2xl p-6 shadow-2xl`}>
                    <div className={link.dropdown === "services" ? "grid grid-cols-2 gap-1" : "flex flex-col gap-1"}>
                      {getDropdownItems(link.dropdown).map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-purple-900/20 transition-colors group"
                        >
                          <div className="w-2 h-2 rounded-full bg-purple-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          <span className="text-gray-300 text-sm group-hover:text-white transition-colors">{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors hover:text-purple-400 ${
                  location.pathname === link.href ? "text-white" : "text-gray-400"
                }`}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {link.label}
              </Link>
            )
          )}
        </nav>

        {/* CTA Button */}
        <Link
          to="/contact"
          className="hidden lg:flex items-center gap-2 cta-button text-white px-6 py-3 rounded-full text-sm font-semibold"
          data-testid="nav-contact-btn"
        >
          Contact Us <Mail size={16} />
        </Link>

        {/* Mobile Menu Toggle */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden text-white p-2"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="lg:hidden absolute top-full left-0 right-0 bg-[#0d0d1a]/95 backdrop-blur-xl border-t border-purple-900/30 p-6 max-h-[80vh] overflow-y-auto">
          <nav className="flex flex-col gap-2">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.label}>
                  <button
                    onClick={() => setMobileDropdown(mobileDropdown === link.dropdown ? null : link.dropdown)}
                    className="text-gray-300 hover:text-purple-400 text-base font-medium py-2 flex items-center gap-2 w-full"
                  >
                    {link.label}
                    <ChevronDown size={16} className={`transition-transform ${mobileDropdown === link.dropdown ? "rotate-180" : ""}`} />
                  </button>
                  {mobileDropdown === link.dropdown && (
                    <div className="ml-4 flex flex-col gap-1 border-l border-purple-900/30 pl-4">
                      {getDropdownItems(link.dropdown).map((item) => (
                        <Link
                          key={item.href}
                          to={item.href}
                          className="text-gray-400 hover:text-purple-400 text-sm py-2 transition-colors"
                          onClick={() => setMobileOpen(false)}
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-gray-300 hover:text-purple-400 text-base font-medium py-2"
                >
                  {link.label}
                </Link>
              )
            )}
            <Link
              to="/contact"
              className="cta-button text-white px-6 py-3 rounded-full text-sm font-semibold text-center mt-4"
              onClick={() => setMobileOpen(false)}
            >
              Contact Us
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
